import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../../hooks/useAuth';
import { LedgerField, LedgerInput } from '../ui';

const validationSchema = yup.object({
  email: yup.string().email('Enter a valid email').required('Email is required'),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password should be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number',
    ),
  passwordConfirm: yup
    .string()
    .required('Password confirmation is required')
    // yup's .oneOf() is an internal "presence" test that only skips null/undefined,
    // never '' — so it would fail an empty confirm against a filled password with
    // "Passwords must match" before .required()'s own empty check ever runs. A plain
    // .test() that no-ops on empty values lets .required() own the empty case.
    .test('passwords-match', 'Passwords must match', function (value) {
      if (!value) return true;
      return value === this.parent.password;
    }),
  firstName: yup.string().required('First name is required').min(2, 'First name should be at least 2 characters'),
  lastName: yup.string().required('Last name is required').min(2, 'Last name should be at least 2 characters'),
  // Empty string passes the * quantifier, so the field stays optional without
  // .nullable() (which fights the resolver's inferred types).
  phoneNumber: yup.string().matches(/^[\d\s\-+()]*$/, 'Enter a valid phone number').default(''),
});

type RegisterValues = yup.InferType<typeof validationSchema>;

interface RegisterFormProps {
  onSuccess?: () => void;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess }) => {
  const { register: registerUser, isLoading, error } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterValues>({ resolver: yupResolver(validationSchema), mode: 'onTouched' });

  const onSubmit = async (values: RegisterValues) => {
    try {
      await registerUser(values);
      onSuccess?.();
    } catch {
      // The auth slice owns the error state.
    }
  };

  return (
    <div>
      <h2 className="mb-3 font-serif text-scale-3 text-ink">Create account</h2>
      {error && (
        <p role="alert" className="mb-3 border border-sev-critical/40 px-3 py-2 text-scale-1 text-sev-critical">
          {error}
        </p>
      )}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <LedgerField label="First name" htmlFor="firstName" error={errors.firstName?.message}>
            <LedgerInput id="firstName" autoComplete="given-name" invalid={!!errors.firstName} {...register('firstName')} />
          </LedgerField>
          <LedgerField label="Last name" htmlFor="lastName" error={errors.lastName?.message}>
            <LedgerInput id="lastName" autoComplete="family-name" invalid={!!errors.lastName} {...register('lastName')} />
          </LedgerField>
        </div>
        <LedgerField label="Email address" htmlFor="email" error={errors.email?.message}>
          <LedgerInput id="email" type="email" autoComplete="email" invalid={!!errors.email} {...register('email')} />
        </LedgerField>
        <LedgerField label="Phone number" htmlFor="phoneNumber" error={errors.phoneNumber?.message} hint="Optional">
          <LedgerInput id="phoneNumber" autoComplete="tel" invalid={!!errors.phoneNumber} {...register('phoneNumber')} />
        </LedgerField>
        <LedgerField label="Password" htmlFor="password" error={errors.password?.message}>
          <LedgerInput id="password" revealToggle autoComplete="new-password" invalid={!!errors.password} {...register('password')} />
        </LedgerField>
        <LedgerField label="Confirm password" htmlFor="passwordConfirm" error={errors.passwordConfirm?.message}>
          <LedgerInput id="passwordConfirm" revealToggle autoComplete="new-password" invalid={!!errors.passwordConfirm} {...register('passwordConfirm')} />
        </LedgerField>
        <button
          type="submit"
          disabled={isLoading}
          className="mt-1 w-full cursor-pointer bg-accent px-3 py-1.5 text-scale-1 font-semibold text-paper hover:bg-accent-hover disabled:opacity-60"
        >
          {isLoading ? 'Creating account…' : 'Create account'}
        </button>
      </form>
      <p className="mt-4 text-center text-scale-1 text-ink-muted">
        Already have an account?{' '}
        <RouterLink to="/login" className="text-accent hover:underline">
          Sign in
        </RouterLink>
      </p>
    </div>
  );
};
